import { AccountSection } from "../AccountSection";
import type { UserSettings } from "../useUserSettings";
import { SettingsSection } from "./SettingsSection";

/**
 * ROW 7 — "My profile" (D-206-010 row 7, kept as value + link; the page itself is rebuilt in T0306).
 *
 * **Who does what:** the person keeps the name, photograph and language Raisy writes and addresses
 * him with; Raisy uses them so he never re-types what is already known.
 * **What he sees:** the identity Raisy currently holds about him, taken from his OWN entity rather
 * than from a form — which is the PO's instruction for this row: *"every user has an entity. we must
 * use also our entity system here and give the user a chance to scrape information about himself to
 * fully be in sync"* (IN-05).
 * **What he feels:** *"never re-type what's known"* (FTUE W6).
 *
 * WHY THIS ROW READS THE ENTITY AND NOTHING ELSE — AND WHY THAT MATTERS MORE THAN IT SOUNDS: it was
 * written while the page it links to (`ProfilePage`) was still the inherited CRM template's profile,
 * which carried a card advertising an MCP server at a Supabase edge function that does not exist in
 * Raisy — so the row stated the truth from the ENTITY, the source the rest of the product reads,
 * rather than echoing the template's separate idea of what a profile is. **T0306 has since done the
 * same to the page itself**: it now reads and writes the person's own entity, the card is gone, and
 * the page can bring facts in from a page he names. This row and that page now agree by construction
 * because they read the same source.
 */
export const ProfileSection = ({ settings }: { settings: UserSettings }) => {
  const { person } = settings;

  const value =
    person.state === "loading" ? (
      <span className="text-muted-foreground">Reading your profile…</span>
    ) : person.state === "unavailable" ? (
      <span className="text-muted-foreground">
        Not readable right now — {person.reason}.
      </span>
    ) : (
      <span className="flex flex-col gap-1">
        <span>{person.value.entity_name || "your name is not set yet"}</span>
        <span className="text-xs font-normal text-muted-foreground">
          {[
            person.value.entity_type,
            person.value.entity_description ? "description set" : null,
            person.value.entity_links?.website ? "website linked" : null,
          ]
            .filter(Boolean)
            .join(" · ") || "nothing else recorded yet"}
        </span>
      </span>
    );

  return (
    <SettingsSection
      title="My profile"
      hint="What Raisy knows about you, so it never asks you to type it twice."
      value={value}
      link={{ to: "/profile", label: "Open your profile" }}
      detail={<AccountSection />}
    />
  );
};
